/**
 * candleFlameFactory — placeable candle / torch / fire-pit light source.
 *
 * Each candle consists of:
 *   - a bright inner flame core (vertically stretched glow_tight)
 *   - a warm outer halo (soft additive glow)
 *   - optional rising ember particles
 *
 * The factory only mints the static sprites; the candleEffect builder owns the
 * per-frame flicker + breath + ember animation. This keeps the factory cheap and
 * testable.
 */
import { createGlowSprite } from './glowSpriteFactory';
import type { ISprite } from '../../rendering/IRenderer';
import { Z_LAYERS } from '../../rendering/Z_LAYERS';
import type { GlowTextureKey } from '../../rendering/glowTextures';

export interface CandleFlameOpts {
  x: number;
  y: number;
  /** Flame core colour (0xRRGGBB). */
  color: number;
  /** Outer halo colour. Defaults to a desaturated/warmer version of `color`. */
  haloColor?: number;
  /** Overall flame height in px (from base to tip). Default 40. */
  height?: number;
  /** Base width of the flame in px. Default 14. */
  width?: number;
  /** Mean alpha of the flame core. Default 0.9. */
  alpha?: number;
  /** Halo diameter relative to flame height. Default 3.0. */
  haloScale?: number;
  /** Z depth. Default Z_LAYERS.PROP_LIGHTS (above terrain, below entities). */
  depth?: number;
  /** Optional per-layer display container. */
  parent?: { addChild(child: unknown): void };
  /** Texture overrides. */
  coreTextureKey?: GlowTextureKey;
  haloTextureKey?: GlowTextureKey;
  /** Disable the halo (e.g. for tiny candles). */
  noHalo?: boolean;
  /** Sprite factory injectable for tests. */
  spriteFactory?: Parameters<typeof createGlowSprite>[0]['spriteFactory'];
}

export interface CandleFlameParts {
  /** The bright inner flame. */
  core: ISprite;
  /** The soft outer halo; null when noHalo is true. */
  halo: ISprite | null;
  /** Destroy all parts. */
  destroy(): void;
}

/**
 * Warm a colour toward orange by pushing green/blue toward the warm range.
 * Used as the default halo colour when none is supplied.
 */
function warmHaloColor(color: number): number {
  const r = (color >> 16) & 0xff;
  const g = Math.max(0, ((color >> 8) & 0xff) - 20);
  const b = Math.max(0, (color & 0xff) - 40);
  return (r << 16) | (g << 8) | b;
}

export function createCandleFlame(o: CandleFlameOpts): CandleFlameParts | null {
  const height = o.height ?? 40;
  const width = o.width ?? 14;
  const alpha = o.alpha ?? 0.9;
  const haloScale = o.haloScale ?? 3.0;
  const depth = o.depth ?? Z_LAYERS.PROP_LIGHTS;
  const haloColor = o.haloColor ?? warmHaloColor(o.color);

  const core = createGlowSprite({
    x: o.x,
    y: o.y,
    size: width,
    color: o.color,
    alpha,
    textureKey: o.coreTextureKey ?? 'glow_tight',
    blendMode: 'add',
    depth,
    parent: o.parent,
    spriteFactory: o.spriteFactory,
  });
  if (!core) return null;

  // Shape the core by display pixels, not raw texture scale. createGlowSprite
  // already sizes its 128px texture to `width`; overwriting that scale would
  // expand the core back to texture size and turn a candle into a light column.
  core.setDisplaySize(width, height);
  // Anchor the display shape at the bottom so the tip moves, not the base.
  core.setOrigin(0.5, 1);

  let halo: ISprite | null = null;
  if (!o.noHalo) {
    halo = createGlowSprite({
      x: o.x,
      y: o.y - height * 0.15,
      size: height * haloScale,
      color: haloColor,
      alpha: 0.35,
      textureKey: o.haloTextureKey ?? 'glow_soft',
      blendMode: 'add',
      depth: depth - 1,
      parent: o.parent,
      spriteFactory: o.spriteFactory,
    });
  }

  return {
    core,
    halo,
    destroy() {
      core.destroy();
      halo?.destroy();
    },
  };
}
